import { useQueryClient } from '@tanstack/react-query'
import { Button, Flex, Form, Modal } from 'antd'
import { ModalProps } from 'antd/lib'
import dayjs from 'dayjs'

import {
  useInvestigationsServiceGetListSpecimensEndpointKey,
  useInvestigationsServiceGetSpecimenEndpoint,
  useInvestigationsServiceRejectSpecimenEndpoint,
  useInvestigationsServiceUpdateSpecimenEndpoint
} from 'src/api/queries'
import {
  ApiError,
  ClassifierCategory,
  ClassifierType,
  SpecimenLaboratoryStatus
} from 'src/api/requests'
import {
  ClassifierInfiniteSelect,
  message,
  SaveConfirm,
  Text
} from 'src/components'
import { getFullName } from 'src/features/users/utils'
import { useClassifierId } from 'src/hooks'
import { dayMonthYearHourMinutesFormatWithDot } from 'src/libs'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

type Props = {
  id?: string
  onCancel: () => void
} & ModalProps

export const RejectInvestigationModal = ({ id, onCancel, ...props }: Props) => {
  const { t } = useLocales()

  const reasonClassifierId = useClassifierId(
    ClassifierType.CANCELLATION_REASON,
    ClassifierCategory.INVESTIGATIONS
  )
  const queryClient = useQueryClient()
  const [form] = Form.useForm()

  const { data, isLoading } = useInvestigationsServiceGetSpecimenEndpoint(
    {
      id: String(id)
    },
    [id],
    { enabled: !!id }
  )

  const { mutate, isPending } = useInvestigationsServiceUpdateSpecimenEndpoint({
    onError: (e: ApiError) => formErrorHandler(e.body?.errors),
    onSuccess: () => {
      const values = form.getFieldsValue(true)
      rejectMutate({
        id: String(id),
        requestBody: { reasonId: values.reasonId }
      })
    }
  })

  const { mutate: rejectMutate } =
    useInvestigationsServiceRejectSpecimenEndpoint({
      onError: (e: ApiError) => formErrorHandler(e.body?.errors),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [useInvestigationsServiceGetListSpecimensEndpointKey]
        })
        message.success(t('investigations.reject_investigation_success'))
        onCancel()
      }
    })

  const onValidate = () => {
    mutate({
      id: String(id),
      requestBody: { laboratoryStatus: SpecimenLaboratoryStatus.REJECTED }
    })
  }

  return (
    <Modal
      width={520}
      footer={null}
      maskClosable={false}
      title={t('investigations.reject_investigation')}
      onCancel={onCancel}
      loading={isLoading}
      open
      {...props}
    >
      <Flex vertical gap={24}>
        <Flex vertical gap="4px">
          <Text>{t('common.comments')}</Text>
          <Text weight={400}>
            <Flex vertical gap="4px">
              {data?.comments?.map((c) => (
                <div key={c.id}>
                  {c.text} -{' '}
                  {getFullName(
                    c.createdByUser?.lastName,
                    c.createdByUser?.firstName
                  )}
                  {', '}
                  {c.createdByUser?.institution?.name},{' '}
                  {dayjs(c.createDate).format(
                    dayMonthYearHourMinutesFormatWithDot
                  )}
                </div>
              ))}
            </Flex>
          </Text>
        </Flex>

        <Form form={form} layout="vertical" requiredMark={false}>
          {reasonClassifierId && (
            <Form.Item
              name="reasonId"
              label={t('investigations.reject_reason')}
            >
              <ClassifierInfiniteSelect
                id={reasonClassifierId}
                placeholder={t('investigations.reject_reason')}
              />
            </Form.Item>
          )}
        </Form>

        <Flex justify="center" gap="16px">
          <Button onClick={onCancel} loading={isPending}>
            {t('common.cancel')}
          </Button>

          <SaveConfirm
            title={`${t('investigations.reject')}?`}
            onConfirm={onValidate}
            okButtonProps={{ loading: isPending }}
          >
            <Button type="primary">{t('investigations.reject')}</Button>
          </SaveConfirm>
        </Flex>
      </Flex>
    </Modal>
  )
}
